import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Layout from "src/components/app/layout/Layout";
import Header from "src/components/app/layout/Header";
import Badge from "src/components/common/Badge";
import {
  Calendar,
  CheckCircle,
  Clock,
  Ellipsis,
  Eye,
  FileText,
  FileTextIcon,
  LucideBox,
  LucideFileText,
  LucideLockKeyhole,
  LucideMapPin,
  LucideTestTubes,
  PlusIcon,
  Share,
  UploadIcon,
} from "lucide-react";
import moment from "moment";
import Button from "src/components/app/Button";
import environment from "src/constants/environment";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as recordsActions from "src/redux/slices/recordsSlice";
import * as journeyActions from "src/redux/slices/journeySlice";
import {
  DOCUMENT_REVIEW_STATUS,
  POST_MESSAGE_EVENTS,
} from "src/constants/common";
import { toast } from "sonner";
import { encryptData } from "src/helpers/encrypt-decrypt";
import UploadChemoCycleDocumentSheet from "src/components/app/bottom-sheet/UploadChemoCycleDocumentSheet";
import ProgramDocumentCard from "src/components/onboarding/program-documents/ProgramDocumentCard";
import { COMPLETED_CYCLE_VISIT_STATUS } from "src/constants/journey";
import LoaderFullScreen from "src/components/common/Loader/LoaderFullScreen";
import Popover from "src/components/common/Popover";
import { sendMessageToParent } from "src/helpers/common";
import Shimmer from "src/components/common/Shimmer";
import { CycleItemType } from "src/types/journey.types";
import {
  DATE_FORMAT_STRING,
  TIME_FORMAT_STRING,
} from "src/constants/dataTimeFormats";
import Loader from "src/components/common/Loader";
import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";

const CycleDetailsScreen: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { cycle, type } = location.state || {};
  const { patient } = useAppSelector((state) => state.auth);
  const { cycleDocuments } = useAppSelector((state) => state.journey);
  const supportProgramKey = params["support-program-key"];
  const [tabs, setTabs] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>("delivery");
  const [showUploadSheet, setShowUploadSheet] = useState(false);
  const [docType, setDocType] = useState<string | "ds" | "pi" | "tr">("ds");
  const [isUploading, setIsUploading] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);

  const getCycleDocuments = () => {
    // const payload = {
    //   page: "1",
    //   // limit: "10",
    //   record_meta_info: { visit_id: cycle?.patient_visit_id },
    //   patient_id: cycle?.patient_id,
    // };
    const payload = {
      page: "1",
      limit: "10",
      record_meta_info: {
        visit_id: cycle.patient_visit_id,
      },
      sort: "-1",
      sortColumn: "test_name,",
    };
    new Promise((resolve, reject) => {
      dispatch(
        journeyActions.getCycleDocumentsRequest({ payload, resolve, reject })
      );
    });
  };

  useEffect(() => {
    // Event: CYCLE_DETAILS_SCREEN_VIEWED
    analytics.pageView(SCREEN_NAMES.CYCLE_DETAILS_SCREEN);
    getCycleDocuments();
  }, []);

  let baseTabs = [
    {
      label: "Delivery",
      value: "delivery",
      icon: <LucideBox className="w-4 h-4" />,
    },
    {
      label: "Documents",
      value: "documents",
      icon: <LucideFileText className="w-4 h-4" />,
    },
    {
      label: "Labs",
      value: "test-reports",
      icon: <LucideTestTubes className="w-4 h-4" />,
    },
  ];

  useEffect(() => {
    if (cycle?.visit_meta_info?.is_free_drug_claimed !== "1") {
      setTabs(baseTabs.filter((tab) => tab.value !== "delivery"));
      setActiveTab("documents");
    } else if (type === "documents") {
      setTabs(baseTabs);
      setActiveTab("documents");
    } else {
      setTabs(baseTabs);
      setActiveTab("delivery");
    }
  }, [cycle]);

  const getOrdinal = (n: number): string => {
    const suffixes = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
  };

  const isCycleCompleted = (status: string) => {
    return COMPLETED_CYCLE_VISIT_STATUS.includes(status);
  };

  const handleFileUpload = async ({
    fileData,
    prevItem,
    isUploadAgain,
  }: {
    fileData: any;
    prevItem: any;
    isUploadAgain: boolean;
  }) => {
    setShowUploadSheet(false);
    setIsUploading(true);
    const formData = new FormData();

    let payload = {
      ...(isUploadAgain && {
        test_type_id: cycle?.test_type_id,
        patient_records_id: cycle?.patient_records_id,
      }),
      title:
        docType === "ds"
          ? "Discharge Summary"
          : docType === "pi"
          ? "Purchase Invoice"
          : "Lab Tests",
      test_name:
        docType === "ds"
          ? "Discharge Summary"
          : docType === "pi"
          ? "Invoice"
          : "Lab Tests",
      container_account: environment.AZURE_CONTAINER_ACCOUNT,
      document_data: [
        isUploadAgain
          ? prevItem?.title
          : `${
              (patient?.data as any)?.patient_id
            }-New-Record-${moment().unix()}.pdf`,
        `${
          (patient?.data as any)?.patient_id
        }-New-Record-${moment().unix()}.pdf`,
      ],
      container_name: "patient-reports",
      extension_type: fileData?.[0]?.type !== "application/pdf" ? "jpg" : "pdf",
      meta_data: {
        onco_cycle_report: "1",
        report_status: DOCUMENT_REVIEW_STATUS.UNDER_REVIEW,
        visit_id: cycle?.patient_visit_id,
      },
    };

    // This will change for every PSP
    //   payload["meta_data"][uploadReportType] = "1";

    // Handle file upload
    const files = Array.isArray(fileData) ? fileData : [fileData];

    // Append files to FormData
    for (const file of files) {
      formData.append("file", file);
    }

    formData.append("payload", encryptData(payload));

    new Promise((resolve, reject) => {
      dispatch(
        recordsActions.updateHealthRecordRequest({
          payload: formData,
          resolve,
          reject,
        })
      );
    })
      .then((res) => {
        // Event: RECORD_UPLOAD_SUCCESS
        analytics.trackEvent(EVENT_NAMES.RECORD_UPLOAD_SUCCESS, {
          screen_name: SCREEN_NAMES.CYCLE_DETAILS_SCREEN,
          support_program_key: supportProgramKey,
          card_title: "Upload Record Bottom Sheet",
          ...payload,
        });
        getCycleDocuments();
        setIsUploading(false);
        toast.success("Record uploaded successfully");
      })
      .catch((error) => {
        // Event: RECORD_UPLOAD_ERROR
        analytics.trackEvent(EVENT_NAMES.RECORD_UPLOAD_ERROR, {
          screen_name: SCREEN_NAMES.CYCLE_DETAILS_SCREEN,
          support_program_key: supportProgramKey,
          card_title: "Upload Record Bottom Sheet",
          ...payload,
        });
        setShowUploadSheet(false);
        setIsUploading(false);
        toast.error("Failed to upload record");
      });
  };

  const getDrugNames = (drugs: any[]) => {
    return drugs.map((drug: any) => {
      return `${String(drug?.drug_label || "")}${
        drug?.is_free === "1" || drug?.is_free === 1 ? " (Free)" : ""
      }`;
    });
  };

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      {isUploading && <LoaderFullScreen show={isUploading} />}
      <Header
        title={`${getOrdinal(cycle?.visit_cycle)} Cycle Details`}
        titlePosition="center"
        showBackButton={true}
        onBackClick={() => {
          navigate(-1);
        }}
      />
      <div
        className={`relative w-full px-4 py-4 bg-white shadow ${
          isCameraActive ? "hidden" : ""
        }`}
      >
        <div className="w-full p-4 bg-gray-50 rounded-lg">
          <div className="w-full flex flex-col gap-2">
            <div className="w-full flex items-start justify-between gap-2">
              <h6 className="text-sm font-bold">
                {getOrdinal(cycle?.visit_cycle)} Cycle
              </h6>
              <Badge
                type={
                  isCycleCompleted(cycle.visit_status) ? "success" : "primary"
                }
              >
                {isCycleCompleted(cycle.visit_status)
                  ? "Completed"
                  : "Upcoming"}
              </Badge>
            </div>
            <p className="text-xs text-gray-500">
              <span className="font-semibold">Completed On:</span>{" "}
              {isCycleCompleted(cycle.visit_status)
                ? moment(cycle.rescheduled_date).format(DATE_FORMAT_STRING)
                : "N/A"}
            </p>
            <p className="text-xs text-gray-500">
              <span className="font-semibold">Primary Drug: </span>
              {cycle?.visit_meta_info?.primary_drugs?.length > 0
                ? getDrugNames(cycle?.visit_meta_info?.primary_drugs).join(", ")
                : "N/A"}
            </p>
            {cycle?.visit_meta_info?.secondary_drugs?.length > 0 && (
              <p className="text-xs text-gray-500">
                <span className="font-semibold">Secondary Drug: </span>
                {cycle?.visit_meta_info?.secondary_drugs?.length > 0
                  ? getDrugNames(cycle?.visit_meta_info?.secondary_drugs).join(
                      ", "
                    )
                  : "N/A"}
              </p>
            )}
            <p className="text-xs text-gray-500">
              <span className="font-semibold">Therapy Indication:</span>{" "}
              {cycle?.visit_meta_info?.therapy_indication ||
                cycle?.visit_meta_info?.therapy_type}
            </p>
          </div>
        </div>
        <div className="w-full border-t border-gray-200 mt-4 pt-4">
          <div className="flex items-center bg-gray-100 p-1 rounded-lg">
            {tabs.map((tab, index) => (
              <button
                type="button"
                key={index.toString()}
                className={`flex-1 w-full px-4 py-2 flex items-center justify-center gap-2 text-xs rounded-md ${
                  activeTab === tab.value
                    ? "bg-white shadow-md shadow-black/5 text-[var(--app-primary)]"
                    : "bg-transparent text-gray-600"
                }`}
                onClick={() => setActiveTab(tab.value)}
              >
                {/* {tab.icon} */}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div
        className={`relative w-full px-4 py-4 overflow-y-auto h-[calc(100vh-57px-255px)] z-0 ${
          isCameraActive ? "hidden" : ""
        }`}
      >
        {activeTab === "delivery" && <DeleveryProgressCard cycle={cycle} />}
        {activeTab === "documents" && (
          <DocumentsProgressCard
            cycle={cycle}
            getCycleDocuments={getCycleDocuments}
            setIsUploading={setIsUploading}
            setShowUploadSheet={setShowUploadSheet}
            setDocType={setDocType}
            isCycleCompleted={isCycleCompleted(cycle.visit_status)}
          />
        )}
        {activeTab === "test-reports" && (
          <TestReportsProgressCard
            cycle={cycle}
            setShowUploadSheet={setShowUploadSheet}
            setDocType={setDocType}
            isCycleCompleted={isCycleCompleted(cycle.visit_status)}
          />
        )}
      </div>
      <UploadChemoCycleDocumentSheet
        showBottomSheet={showUploadSheet}
        setShowBottomSheet={setShowUploadSheet}
        onFileUpload={handleFileUpload}
        //onCameraStateChange={setIsCameraActive}
        isUploadAgain={
          cycleDocuments?.data?.documents?.find(
            (document) =>
              document?.record_meta_info?.report_status ===
              DOCUMENT_REVIEW_STATUS.REJECTED
          )
            ? true
            : false
        }
      />
    </Layout>
  );
};

const DeleveryProgressCard = ({ cycle }: { cycle: CycleItemType }) => {
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const orderTimeline = useMemo(() => {
    const rawOrderDate = cycle?.visit_meta_info?.order_date;

    if (!rawOrderDate) {
      return null;
    }

    try {
      if (typeof rawOrderDate === "string") {
        return JSON.parse(rawOrderDate);
      }
      return rawOrderDate;
    } catch (error) {
      console.error("Failed to parse order timeline", error);
      return null;
    }
  }, [cycle?.visit_meta_info?.order_date]);

  const baseDeliveries = useMemo(
    () => [
      {
        title: "Ordered",
        description: "Your medication has been ordered",
        status: "in-progress",
      },
      {
        title: "Shipped",
        description: "Your medication has been shipped",
        status: "in-progress",
      },

      {
        title: "Delivered",
        description: "Your medication has been delivered",
        status: "in-progress",
      },
    ],
    []
  );

  useEffect(() => {
    const statusOrder = ["Ordered", "Shipped", "Delivered"];
    const currentStatus = cycle?.visit_meta_info?.order_status;
    const currentIndex = statusOrder.indexOf(currentStatus);

    if (currentIndex === -1) {
      setDeliveries(baseDeliveries);
      return;
    }

    setDeliveries(
      baseDeliveries.map((delivery, index) =>
        index <= currentIndex ? { ...delivery, status: "success" } : delivery
      )
    );
  }, [cycle, baseDeliveries]);

  const getTimelineDate = (title: string) => {
    if (!orderTimeline) {
      return "";
    }

    const timelineKeys: Record<string, string> = {
      Ordered: orderTimeline?.ordered_date,
      Shipped: orderTimeline?.shipped_date,
      Delivered: orderTimeline?.delivered_date,
    };

    return timelineKeys[title] || "";
  };

  const getTimelineLabel = (title: string) => {
    const labels: Record<string, string> = {
      Ordered: "Order date",
      Shipped: "Shipment date",
      Delivered: "Delivery date",
    };

    return labels[title] || "Date";
  };

  const getTimelineDescription = (delivery: {
    title: string;
    description: string;
  }) => {
    const timelineDate = getTimelineDate(delivery.title);

    if (!timelineDate) {
      return delivery.description;
    }

    return `${delivery.description} on ${moment(timelineDate).format(
      "DD MMM, YYYY"
    )}`;
  };

  return (
    <div className="w-full p-4 bg-white rounded-lg">
      <div className="relative w-full mb-4">
        <h6 className="text-sm font-bold">Delivery Progress</h6>
      </div>
      <div className="flex flex-col gap-4">
        {deliveries.map((delivery, index) => (
          <div
            key={index.toString()}
            className="flex-1 flex items-center gap-3"
          >
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-white ${
                delivery.status === "success" ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              {delivery.status === "success" ? (
                <CheckCircle className="w-3.5 h-3.5" />
              ) : (
                <Clock className="w-3.5 h-3.5" />
              )}
            </div>
            <div>
              <h6 className="text-sm font-medium">{delivery.title}</h6>
              <p className="text-xs text-gray-500">
                {getTimelineDescription(delivery)}
              </p>
            </div>
          </div>
        ))}
        <div className="relative w-full bg-gray-50 p-2 rounded-md flex items-start gap-2">
          <div className="mt-1">
            <LucideMapPin className="w-3.5 h-3.5" />
          </div>
          <div>
            <h6 className="text-sm font-semibold mb-1">Delivery Address</h6>
            <p className="text-xs text-gray-500">
              {cycle?.visit_meta_info?.order_address}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const DocumentsProgressCard = ({
  cycle,
  getCycleDocuments,
  setIsUploading,
  setShowUploadSheet,
  setDocType,
  isCycleCompleted,
}: {
  cycle: CycleItemType;
  getCycleDocuments: () => void;
  setIsUploading: (isUploading: boolean) => void;
  setShowUploadSheet: (showUploadSheet: boolean) => void;
  setDocType: (docType: string) => void;
  isCycleCompleted: boolean;
}) => {
  const { cycleDocuments } = useAppSelector((state) => state.journey);
  const params = useParams();
  const supportProgramKey = params["support-program-key"];

  const hasDischargeSummary = cycleDocuments?.data?.documents?.some(
    (document) => document.test_name === "Discharge Summary"
  )
    ? true
    : false;

  const hasInvoice = cycleDocuments?.data?.documents?.some(
    (document) => document.test_name === "Invoice"
  )
    ? true
    : false;

  const isPurchaseInvoiceRequired =
    cycle?.visit_meta_info?.is_purchase_invoice_required === "1";

  const LockDocumentUpload = () => {
    return (
      <div className="absolute top-0 right-0 w-full h-full p-2 bg-black/30 rounded-lg flex items-center justify-center">
        <LucideLockKeyhole className="w-10 h-10 text-white" />
      </div>
    );
  };

  const handleUploadDocument = (docType: "ds" | "pi") => {
    // Event: DOCUMENT_UPLOAD_CLICKED
    analytics.trackEvent(
      `${
        docType === "ds"
          ? "discharge_summary"
          : docType === "pi"
          ? "purchase_invoice"
          : "other"
      }_${EVENT_NAMES.RECORD_UPLOAD_CLICKED}`,
      {
        screen_name: SCREEN_NAMES.CYCLE_DETAILS_SCREEN,
        support_program_key: supportProgramKey,
        ...cycle,
        document_type:
          docType === "ds"
            ? "Discharge Summary"
            : docType === "pi"
            ? "Purchase Invoice"
            : "Other",
      }
    );
    setShowUploadSheet(true);
    setDocType(docType);
  };

  return (
    <>
      <div className="w-full p-4 bg-white rounded-lg">
        <div className="relative w-full mb-4">
          <h6 className="text-sm font-bold">Upload Documents</h6>
        </div>

        {cycleDocuments?.isLoading ? (
          <div className="flex items-center justify-center h-[250px] gap-4">
            <Loader />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {cycleDocuments?.data?.documents?.map((document, index) => (
              <ProgramDocumentCard
                key={index.toString()}
                documentData={document}
                document={document}
                onUploadingStart={() => setIsUploading(true)}
                documentType={
                  document.test_name === "Discharge Summary" ? "ds" : "pi"
                }
                onSuccess={() => {
                  getCycleDocuments();
                  setIsUploading(false);
                }}
              />
            ))}

            {!hasDischargeSummary && (
              <div className="relative w-full p-4 rounded-lg border-2 border-gray-200 border-dashed">
                <div className="flex items-center justify-center flex-col gap-2">
                  <div className="flex flex-col items-center justify-center text-center gap-2 mb-2">
                    <FileTextIcon className="w-5 h-5 text-gray-500" />
                    <h6 className="text-sm font-semibold">Discharge Summary</h6>
                    <p className="text-sm text-gray-500">
                      Upload your latest discharge summary
                    </p>
                  </div>
                  <Button
                    variant="outlined"
                    rounded="full"
                    onClick={() => handleUploadDocument("ds")}
                  >
                    <UploadIcon className="w-4 h-4 mr-2" /> Upload Document
                  </Button>
                </div>
                {!isCycleCompleted && <LockDocumentUpload />}
              </div>
            )}

            {!hasInvoice && isPurchaseInvoiceRequired && (
              <div className="relative w-full p-4 rounded-lg border-2 border-gray-200 border-dashed">
                <div className="flex items-center justify-center flex-col gap-2">
                  <div className="flex flex-col items-center justify-center text-center gap-2 mb-2">
                    <FileTextIcon className="w-5 h-5 text-gray-500" />
                    <h6 className="text-sm font-semibold">Purchase Invoice</h6>
                    <p className="text-sm text-gray-500">
                      Upload your latest purchase invoice
                    </p>
                  </div>
                  <Button
                    variant="outlined"
                    rounded="full"
                    onClick={() => handleUploadDocument("pi")}
                  >
                    <UploadIcon className="w-4 h-4 mr-2" /> Upload Document
                  </Button>
                </div>
                {!isCycleCompleted && <LockDocumentUpload />}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

const TestReportsProgressCard = ({
  cycle,
  setShowUploadSheet,
  setDocType,
  isCycleCompleted,
}: {
  cycle: CycleItemType;
  setShowUploadSheet: (showUploadSheet: boolean) => void;
  setDocType: (docType: string) => void;
  isCycleCompleted: boolean;
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const { cycleDocuments } = useAppSelector((state) => state.journey);

  const handleShare = (document: any) => {
    const payload = {
      documentLink:
        Array.isArray(document?.document_url) &&
        document?.document_url?.length > 0
          ? document?.document_url[0]
          : document?.document_url,
      documentName: document?.title,
    };
    sendMessageToParent(POST_MESSAGE_EVENTS.SHARE_RECORD_DOCUMENT, payload);
  };

  const handleViewFile = (document: any) => {
    const payload = {
      document_name: document.document_name,
      title: document.title,
      document_url: document.document_url[0],
    };
    navigate(`/app/${supportProgramKey}/records/preview`, {
      state: {
        record: payload,
      },
    });
  };

  const LockDocumentUpload = () => {
    return (
      <div className="absolute top-0 right-0 w-full h-full p-2 bg-black/30 rounded-lg flex items-center justify-center">
        <LucideLockKeyhole className="w-10 h-10 text-white" />
      </div>
    );
  };

  const handleUploadDocument = (docType: "tr") => {
    // Event: DOCUMENT_UPLOAD_CLICKED
    analytics.trackEvent(
      `${docType === "tr" ? "lab_report" : "lab_test"}_${
        EVENT_NAMES.RECORD_UPLOAD_CLICKED
      }`,
      {
        screen_name: SCREEN_NAMES.CYCLE_DETAILS_SCREEN,
        support_program_key: supportProgramKey,
        document_type: docType === "tr" ? "Lab Report" : "Lab Test",
        card_title: "Upload Lab Reports",
        ...cycle,
      }
    );
    setShowUploadSheet(true);
    setDocType(docType);
  };
  return (
    <div className="w-full p-4 bg-white rounded-lg">
      <div className="relative w-full flex items-center justify-between gap-2 mb-4">
        <h6 className="text-sm font-bold">Upload Lab Reports</h6>
        {cycleDocuments?.data?.testReports?.data?.length > 0 && (
          <Button size="sm" variant="outlined">
            <PlusIcon
              className="w-4 h-4"
              onClick={() => handleUploadDocument("tr")}
            />
          </Button>
        )}
      </div>
      {cycleDocuments.isLoading && (
        <div className="flex flex-col gap-4 mb-4">
          <Shimmer width="100%" height="100px" />
        </div>
      )}
      {!cycleDocuments?.data?.testReports?.data?.length &&
      !cycleDocuments.isLoading ? (
        <div className="flex flex-col gap-4">
          <div className="relative w-full p-4 rounded-lg border-2 border-gray-200 border-dashed">
            <div className="flex items-center justify-center flex-col gap-2">
              <div className="flex flex-col items-center justify-center text-center gap-2 mb-2">
                <LucideTestTubes className="w-5 h-5 text-gray-500" />
                <h6 className="text-sm font-semibold">Lab Reports</h6>
                <p className="text-sm text-gray-500">
                  Upload your latest lab reports
                </p>
              </div>
              <Button
                variant="outlined"
                rounded="full"
                onClick={() => handleUploadDocument("tr")}
              >
                <UploadIcon className="w-4 h-4 mr-2" /> Upload Document
              </Button>
            </div>
            {!isCycleCompleted && <LockDocumentUpload />}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {cycleDocuments?.data?.testReports?.data?.map((report, index) => (
            <div
              key={index}
              className="flex flex-col items-start gap-2 bg-gray-100 p-4 rounded-lg shadow-sm"
            >
              <div className="flex items-start justify-between w-full gap-2">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-white rounded-md">
                    <FileText className="w-5 h-5 text-[var(--app-primary)]" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <p className="text-sm font-medium line-clamp-2">
                      {report.title}
                    </p>
                    <span className="text-xs bg-white px-2 py-1 rounded-md text-gray-500">
                      {report.test_name}
                    </span>
                  </div>
                </div>
                <Popover label={<Ellipsis className="w-5 h-5" />}>
                  <ul className="w-full">
                    <li className="w-full block">
                      <button
                        type="button"
                        className="flex items-center gap-2 text-sm w-full p-2 hover:bg-gray-100 rounded-md"
                        onClick={() => handleViewFile(report)}
                      >
                        <Eye className="w-4 h-4" /> View File
                      </button>
                    </li>

                    <li className="w-full block">
                      <button
                        type="button"
                        className="flex items-center gap-2 text-sm w-full p-2 hover:bg-gray-100 rounded-md"
                        onClick={() => handleShare(report)}
                      >
                        <Share className="w-4 h-4" /> Share File
                      </button>
                    </li>
                  </ul>
                </Popover>
              </div>
              <div className="flex items-center justify-between w-full gap-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <span className="text-xs text-gray-500">
                    {/* {report.created_at} */}
                    {moment(report.created_at).format(
                      DATE_FORMAT_STRING
                    )} •{" "}
                    {moment(report.created_at)
                      .local()
                      .format(TIME_FORMAT_STRING)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CycleDetailsScreen;
